import { Suspense, useRef, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PageState } from "@/components/ui/PageState";
import { usePrediction } from "@/features/prediction/public/context";
import { isPredictionRouteReloadSafe } from "@/features/prediction/public/routeRecovery";
import { isSimulationRouteReloadSafe } from "@/features/simulation-v3/public/routeRecovery";
import { AppShell } from "@/layouts/AppShell/AppShell";
import { lazyRoute } from "./lazyRoute";
import { RouteRecoveryBoundary } from "./routeRecovery";

const WorkspacePage = lazyRoute(() =>
  import("@/pages/WorkspacePage").then((module) => ({
    default: module.WorkspacePage,
  })),
);
const SocietyPage = lazyRoute(() =>
  import("@/pages/SocietyPage").then((module) => ({
    default: module.SocietyPage,
  })),
);
const CounterfactualPage = lazyRoute(() =>
  import("@/pages/CounterfactualPage").then((module) => ({
    default: module.CounterfactualPage,
  })),
);
const HistoryPage = lazyRoute(() =>
  import("@/pages/HistoryPage").then((module) => ({
    default: module.HistoryPage,
  })),
);
const DataPage = lazyRoute(() =>
  import("@/pages/DataPage").then((module) => ({
    default: module.DataPage,
  })),
);
const NotFoundPage = lazyRoute(() =>
  import("@/pages/NotFoundPage").then((module) => ({
    default: module.NotFoundPage,
  })),
);

function RouteFallback() {
  return (
    <div className="page-container">
      <PageState kind="loading" />
    </div>
  );
}

function RecoverableRoute({
  children,
  returnTo,
}: {
  children: ReactNode;
  returnTo: string;
}) {
  const location = useLocation();
  const prediction = usePrediction();
  const canReload = () => isPredictionRouteReloadSafe(
    prediction.draft,
    prediction.run.status,
  ) && isSimulationRouteReloadSafe();
  return (
    <RouteRecoveryBoundary
      key={location.pathname + location.search}
      canReload={canReload}
      returnTo={returnTo}
    >
      <Suspense fallback={<RouteFallback />}>
        {children}
      </Suspense>
    </RouteRecoveryBoundary>
  );
}

export function App() {
  const location = useLocation();
  const lastWorkspaceRoute = useRef("/workspace");
  if (location.pathname === "/workspace") {
    lastWorkspaceRoute.current = location.pathname + location.search;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to="/workspace" replace />} />
          <Route path="/workspace" element={(
            <RecoverableRoute returnTo={lastWorkspaceRoute.current}>
              <WorkspacePage />
            </RecoverableRoute>
          )} />
          <Route path="/society" element={(
            <RecoverableRoute returnTo={lastWorkspaceRoute.current}>
              <SocietyPage />
            </RecoverableRoute>
          )} />
          <Route path="/counterfactual" element={(
            <RecoverableRoute returnTo={lastWorkspaceRoute.current}>
              <CounterfactualPage />
            </RecoverableRoute>
          )} />
          <Route path="/history" element={(
            <RecoverableRoute returnTo={lastWorkspaceRoute.current}>
              <HistoryPage />
            </RecoverableRoute>
          )} />
          <Route path="/data" element={(
            <RecoverableRoute returnTo={lastWorkspaceRoute.current}>
              <DataPage />
            </RecoverableRoute>
          )} />
        </Route>
        <Route path="*" element={(
          <RecoverableRoute returnTo={lastWorkspaceRoute.current}>
            <NotFoundPage />
          </RecoverableRoute>
        )} />
      </Routes>
    </Suspense>
  );
}
